/**
 * ImportDocumentsButton (#396) — importa registros de uma base do tipo
 * DOCUMENTOS (Base de Conhecimento) a partir de CSV.
 *
 * Fluxo: upload CSV → mapeia colunas para campos do documento (título/conteúdo/
 * resumo/autor/palavras-chave) → constrói um `data` VÁLIDO por linha (com
 * defaults de `category`/`itemStatus`/`language`) e cria via o endpoint
 * existente (`useCreateDatabaseDocument`, `metadata.source: 'import'`).
 *
 * Escopo: só DOCUMENTOS. Os tipos de catálogo (produtos/imóveis/veículos/
 * serviços) têm schema estrito com estruturas aninhadas e `opportunities`
 * obrigatório — import por CSV plano não os satisfaz; são geridos pelos forms
 * ricos (ou pelo import por IA, trabalho futuro).
 */

import React, { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { DatabaseType } from '@troia-v3/types';
import { Button, Modal, ModalContent, ModalFooter, ModalHeader, Select, type SelectOption } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { useCreateDatabaseDocument } from '../hooks/useDatabaseDocuments';

/** Campos do documento que o CSV pode preencher. */
const DOC_FIELD_OPTIONS: SelectOption[] = [
  { value: '', label: 'Ignorar coluna' },
  { value: 'title', label: 'Título' },
  { value: 'content', label: 'Conteúdo' },
  { value: 'summary', label: 'Resumo' },
  { value: 'author', label: 'Autor' },
  { value: 'keywords', label: 'Palavras-chave (; )' },
];

/** Sugestão de mapeamento pelo cabeçalho do CSV. */
function suggestField(header: string, index: number): string {
  const h = header.trim().toLowerCase();
  if (/t[ií]tulo|title|nome|assunto/.test(h)) return 'title';
  if (/conte[úu]do|content|texto|body|descri/.test(h)) return 'content';
  if (/resumo|summary|sum[áa]rio/.test(h)) return 'summary';
  if (/autor|author/.test(h)) return 'author';
  if (/palavra|keyword|tag/.test(h)) return 'keywords';
  if (index === 0) return 'title';
  if (index === 1) return 'content';
  return '';
}

function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines: string[][] = [];
  let cur: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { inQuotes = false; }
      } else { field += c; }
    } else if (c === '"') { inQuotes = true; }
    else if (c === ',') { cur.push(field); field = ''; }
    else if (c === '\n') { cur.push(field); lines.push(cur); cur = []; field = ''; }
    else if (c !== '\r') { field += c; }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); lines.push(cur); }
  const headers = (lines[0] || []).map((h) => h.trim());
  const rows = lines.slice(1).filter((r) => r.some((c) => c.trim() !== ''));
  return { headers, rows };
}

export interface ImportDocumentsButtonProps {
  databaseId: string;
  onImported?: () => void;
  /** Botão ocupa a largura disponível (para o rodapé mobile — #407). */
  fullWidth?: boolean;
}

export const ImportDocumentsButton: React.FC<ImportDocumentsButtonProps> = ({ databaseId, onImported, fullWidth }) => {
  const createDoc = useCreateDatabaseDocument<Record<string, unknown>>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'upload' | 'map' | 'importing'>('upload');
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<string[]>([]);
  const [progress, setProgress] = useState(0);

  const reset = (): void => {
    setStep('upload'); setFileName(''); setHeaders([]); setRows([]); setMapping([]); setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const close = (): void => { setOpen(false); reset(); };

  const handleFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    try {
      const parsed = parseCSV(await file.text());
      if (parsed.headers.length === 0 || parsed.rows.length === 0) { toast.error('CSV vazio ou inválido'); return; }
      setFileName(file.name); setHeaders(parsed.headers); setRows(parsed.rows);
      setMapping(parsed.headers.map((h, i) => suggestField(h, i)));
      setStep('map');
    } catch { toast.error('Erro ao ler o arquivo'); }
  };

  const hasTitle = useMemo(() => mapping.includes('title'), [mapping]);

  const handleImport = async (): Promise<void> => {
    setStep('importing');
    let ok = 0;
    let failed = 0;
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r]!;
      const get = (field: string): string => {
        const idx = mapping.indexOf(field);
        return idx >= 0 ? (row[idx] ?? '').trim() : '';
      };
      let title = get('title') || `Registro ${r + 1}`;
      if (title.length < 3) title = `Registro ${r + 1}`;
      const rawContent = get('content');
      // content exige >= 20 chars — fallback garantido para não falhar a validação.
      let content = rawContent;
      if (content.length < 20) content = `${title}\n\n${rawContent || 'Importado via CSV.'}`;
      if (content.length < 20) content = content.padEnd(20, ' ');
      const summary = get('summary');
      const author = get('author');
      const keywords = get('keywords');
      const data: Record<string, unknown> = {
        title: title.slice(0, 300),
        content,
        category: 'article',
        itemStatus: 'draft',
        language: 'pt-BR',
        ...(summary ? { summary: summary.slice(0, 500) } : {}),
        ...(author ? { author } : {}),
        ...(keywords ? { keywords: keywords.split(';').map((k) => k.trim()).filter(Boolean) } : {}),
      };
      try {
        await createDoc.mutateAsync({ databaseId, type: DatabaseType.DOCUMENTS, data, metadata: { source: 'import' } });
        ok++;
      } catch { failed++; }
      setProgress(r + 1);
    }
    onImported?.();
    if (ok > 0) toast.success(`${ok} documento(s) importado(s)${failed > 0 ? ` · ${failed} falha(s)` : ''}`);
    else toast.error('Nenhum documento importado');
    close();
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`flex items-center gap-1.5 h-[38px] rounded-lg text-sm font-semibold border transition-colors ${fullWidth ? 'px-3 flex-1 justify-center' : 'px-2.5 2xl:@tb:px-3 flex-none'}`}
        style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-card-text)', backgroundColor: 'var(--theme-card-background)' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--theme-card-background)'; }}
        title="Importar registros"
        data-testid="records-import"
      >
        <Icon name="upload" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
        {/* Rodapé mobile (fullWidth) mantém o rótulo sempre; na toolbar segue o padrão do DS */}
        <span className={fullWidth ? undefined : 'hidden 2xl:@tb:inline'}>Importar</span>
      </button>

      <Modal isOpen={open} onClose={close} maxWidth="2xl">
        {/* F74: título via prop tipada (18px semibold) */}
        <ModalHeader title="Importar registros" onClose={close} />
        <ModalContent>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => void handleFile(e.target.files?.[0])} data-testid="import-records-input" />

          {step === 'upload' && (
            <button onClick={() => fileInputRef.current?.click()} className="w-full border-2 border-dashed rounded-xl p-10 text-center" style={{ borderColor: 'var(--theme-input-border)' }} data-testid="import-records-dropzone">
              <Icon name="upload" className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--theme-muted-text)' }} />
              <div className="text-sm font-medium" style={{ color: 'var(--theme-text)' }}>Clique para selecionar um CSV</div>
              <div className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>Primeira linha = cabeçalho. Mapeie as colunas na próxima etapa.</div>
            </button>
          )}

          {step === 'map' && (
            <div className="space-y-3" data-testid="import-records-mapping">
              <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
                <b style={{ color: 'var(--theme-text)' }}>{fileName}</b> · {rows.length} linha(s). Mapeie cada coluna para um campo do documento.
              </p>
              <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--theme-card-border)' }}>
                {headers.map((h, idx) => (
                  <div key={idx} className="flex items-center gap-3 px-3 py-2 border-b" style={{ borderColor: 'var(--theme-card-border)' }}>
                    <span className="text-sm w-40 flex-shrink-0 truncate" style={{ color: 'var(--theme-muted-text)' }}>{h || `Coluna ${idx + 1}`}</span>
                    <Icon name="arrow-right" className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }} />
                    <div className="flex-1 min-w-0">
                      <Select options={DOC_FIELD_OPTIONS} value={mapping[idx] ?? ''} onChange={(v) => setMapping((prev) => prev.map((m, i) => (i === idx ? v : m)))} data-testid={`import-map-${idx}`} />
                    </div>
                  </div>
                ))}
              </div>
              {!hasTitle && <p className="text-xs" style={{ color: 'var(--theme-error-color)' }}>Mapeie ao menos uma coluna como <b>Título</b>.</p>}
            </div>
          )}

          {step === 'importing' && (
            <div className="py-8 text-center">
              <p className="text-sm mb-3" style={{ color: 'var(--theme-text)' }}>Importando… {progress}/{rows.length}</p>
              <div className="w-full h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--theme-border)' }}>
                <div className="h-full rounded-full transition-all" style={{ width: `${rows.length ? Math.round((progress / rows.length) * 100) : 0}%`, backgroundColor: 'var(--theme-primary)' }} />
              </div>
            </div>
          )}
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={close} disabled={step === 'importing'} data-testid="import-records-cancel">Cancelar</Button>
          {step === 'map' && (
            <Button icon="check" onClick={() => void handleImport()} disabled={!hasTitle} data-testid="import-records-confirm">
              Importar {rows.length} registro(s)
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

ImportDocumentsButton.displayName = 'ImportDocumentsButton';
